import { canvasBearing, parseWorkArea, serializeWorkArea } from "./work-area.ts";
import type { LngLat, WorkArea } from "./types.ts";

/**
 * A quest's route canvas: the editor canvas the route was drawn in, copied onto the
 * quest with the route line. Its bearing is the map bearing the drawing reads upright at.
 *
 * Stored in `missions.route_canvas` as JSON text. SQL NULL means the quest was bound
 * before canvases were copied (look it up once); the JSON text `null` means the route
 * has no canvas (north-up).
 */
export const NO_ROUTE_CANVAS = "null";

export type StoredRouteCanvas = { known: true; canvas: WorkArea | null } | { known: false };

export function encodeRouteCanvas(area: WorkArea | null | undefined): string {
  return area ? JSON.stringify(serializeWorkArea(area)) : NO_ROUTE_CANVAS;
}

export function decodeRouteCanvas(text: string | null | undefined): StoredRouteCanvas {
  if (text == null) return { known: false };
  try {
    return { known: true, canvas: parseWorkArea(JSON.parse(text)) };
  } catch {
    return { known: true, canvas: null };
  }
}

/** Bearing a quest map opens at. No canvas, or an unrotated one, is north-up. */
export function routeCanvasBearing(area: WorkArea | null | undefined): number {
  return area ? canvasBearing(area) : 0;
}

/** Same route line, allowing float noise from a JSON round trip. */
export function sameRouteLine(a: readonly LngLat[], b: readonly LngLat[], epsDeg = 1e-9): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i]![0] - b[i]![0]) > epsDeg || Math.abs(a[i]![1] - b[i]![1]) > epsDeg) return false;
  }
  return true;
}
